import { roleName, skillName } from "./scenario";

// The practice export (milestone 8): one row per person per week, from
// public.admin_score_export. Levels only, never a score, and the file opens with a
// coaching-only line so it travels with the data wherever the file goes.

export interface ExportRow {
  weekStart: string;
  staffId: string;
  name: string;
  phone: string;
  role: string;
  site: string;
  active: boolean;
  assigned: number;
  completed: number;
  strong: string[];
  improving: string[];
  starting: string[];
}

export interface ScoreExport {
  from: string;
  to: string;
  rows: ExportRow[];
}

export const COACHING_LINE =
  "Coaching only. Use this to plan practice and coaching conversations. Never use it for pay, discipline, appraisal or any HR decision.";

const HEADER = [
  "Week starting",
  "Site",
  "Name",
  "Phone",
  "Role",
  "Still working here",
  "Scenarios given",
  "Scenarios done",
  "Strong skills",
  "Improving skills",
  "Starting skills",
  "Strong",
  "Improving",
  "Starting",
];

/**
 * One CSV field. Quoted when it holds a comma, quote or line break; a leading
 * = + - @ (or tab) is escaped so a spreadsheet never runs a roster name as a formula.
 */
export function csvField(value: string | number): string {
  let text = String(value);
  if (typeof value === "string" && /^[=+\-@\t\r]/.test(text)) text = "'" + text;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const skills = (tags: string[]) => tags.map(skillName).join("; ");

export function exportCsv(data: ScoreExport, company: string, site: string | null, madeOn: string): string {
  const about = `${company} · ${site ?? "All sites"} · weeks from ${data.from} to ${data.to} · made on ${madeOn}`;
  const lines = [
    [COACHING_LINE],
    [about],
    [],
    HEADER,
    ...data.rows.map((r) => [
      r.weekStart,
      r.site,
      r.name,
      r.phone,
      roleName(r.role),
      r.active ? "Yes" : "No",
      r.assigned,
      r.completed,
      r.strong.length,
      r.improving.length,
      r.starting.length,
      skills(r.strong),
      skills(r.improving),
      skills(r.starting),
    ]),
  ];
  // The byte order mark lets Excel read Marathi and Hindi names as UTF-8.
  return "﻿" + lines.map((l) => l.map(csvField).join(",")).join("\r\n") + "\r\n";
}

export function exportFileName(company: string, from: string, to: string): string {
  const slug = company.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "company";
  return `chaukas-practice-${slug}-${from}-to-${to}.csv`;
}

/** The Monday of the week holding this date (YYYY-MM-DD), as the server rounds it. */
export function mondayOf(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

export function addWeeks(iso: string, weeks: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}
